// Rohbeobachtungen -> Leads: die Entity Resolution des Lead-Scouts
// (ERP/CRM-Plan, Abschnitt 5 „Dedupe in drei Ebenen").
//
// Die drei Ebenen und wo sie liegen:
//   1. Roh-Identitaet   UNIQUE(source, source_record_id)  -> lead_scout_queue.sql
//   2. Fingerprint      fingerprintLead() in dieser Datei -> Kandidatenpaare
//   3. Entscheidung     lead_matches                      -> automatisch oder Review
//
// Ebene 2 ist bewusst MEHRSIGNALIG. Normalisierter Name + PLZ allein ist zu
// schwach (Codex-Befund 2.4): „Bau GmbH" gibt es in jedem zweiten Postleitzahl-
// gebiet. Deshalb fliessen Name, PLZ, Web-Domain und Telefonnummer ein.
//
// Gegenstueck im Python-Dienst: lead-scout/app/normalize.py. Die
// Normalisierungsregeln sind dort und hier wortgleich beschrieben, damit
// derselbe Datensatz auf beiden Seiten denselben Kern ergibt.
//
// Der gesamte obere Teil ist PUR (kein Supabase, kein Netz, keine Uhr) — die
// Identitaetsentscheidung muss ohne Infrastruktur reproduzierbar und pruefbar
// sein. Der unreine Teil steht klar getrennt am Ende der Datei.

import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import { ladeAlleSeiten, ZEILEN_JE_SEITE } from './seitenweise';
import { nameSimilarity } from './customerDuplicates';
import { isBranche } from './branchen';
import { addressHash } from './geoCache';

// ═══ TEIL 1 — PURE NORMALISIERUNG ═══════════════════════════════════════════

/**
 * Mehrwort-Rechtsformen. Werden ZUERST entfernt, damit „gmbh co kg" nicht in
 * ein einzelnes ratloses „co" zerfaellt (gleiche Reihenfolge-Begruendung wie
 * in normalize.py).
 */
const RECHTSFORMEN_MEHRWORT: string[] = [
    'gmbh und co kg',
    'gmbh co kg',
    'gmbh co kgaa',
    'ag und co kg',
    'ag co kg',
    'ug haftungsbeschraenkt',
    'gesellschaft mit beschraenkter haftung',
    'eingetragener kaufmann',
    'eingetragene kauffrau',
    'eingetragener verein',
    'partnerschaftsgesellschaft mbb',
    'partnerschaftsgesellschaft',
];

/** Einwort-Rechtsformen und Fuellwoerter ohne Identitaetswert. */
const RECHTSFORMEN_EINWORT: Set<string> = new Set([
    'gmbh', 'mbh', 'ag', 'ug', 'kg', 'kgaa', 'ohg', 'gbr', 'se', 'ek', 'eg', 'ev',
    'ltd', 'limited', 'plc', 'inc', 'bv', 'nv', 'sarl', 'partg', 'mbb', 'co', 'cie',
]);

const UMLAUTE: [RegExp, string][] = [
    [/ä/g, 'ae'],
    [/ö/g, 'oe'],
    [/ü/g, 'ue'],
    [/ß/g, 'ss'],
];

/**
 * Firmenname auf seinen identifizierenden Kern.
 *
 * Kleinschreibung, Umlaute ausgeschrieben, Satzzeichen weg, Rechtsformen weg.
 * '' bei leerer Eingabe — der Aufrufer MUSS '' als „kein Signal" behandeln und
 * niemals als Treffer.
 *
 * Warum ausgeschrieben und nicht gestrippt: „Mueller" und „Müller" sind
 * dieselbe Firma in zwei Schreibweisen, „Muller" ist ein anderer Name. Ein
 * blosses Entfernen des Umlauts wuerfe alle drei zusammen.
 *
 * Beispiel: „Müller & Sohn Bau GmbH & Co. KG" -> „mueller sohn bau"
 */
export const normalizeLeadName = (name?: string | null): string => {
    if (!name) return '';
    let s = String(name).toLowerCase();
    for (const [re, rep] of UMLAUTE) s = s.replace(re, rep);
    // Punkte VOR dem Tokenisieren fallen lassen: „e.K." -> „ek", „Co." -> „co".
    // Sonst wird jede Abkuerzung zu einem eigenen, nie zuordenbaren Token.
    s = s.replace(/\./g, '').replace(/&/g, ' ');
    s = s.replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

    for (const phrase of RECHTSFORMEN_MEHRWORT) s = s.split(phrase).join(' ');

    return s
        .split(' ')
        .filter((t) => t && !RECHTSFORMEN_EINWORT.has(t))
        .join(' ');
};

/**
 * Blanker Host aus URL, E-Mail oder Freitext. Schema, „www." und Pfad fallen
 * weg. Die Domain ist das staerkste Einzelsignal fuer Firmenidentitaet und
 * darum die sorgfaeltigste Normalisierung wert.
 */
export const normalizeDomain = (website?: string | null): string => {
    if (!website) return '';
    let v = String(website).trim().toLowerCase();
    v = v.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
    const at = v.lastIndexOf('@');
    if (at >= 0) v = v.slice(at + 1);
    v = v.split('/')[0].split('?')[0].split('#')[0].split(':')[0];
    if (v.startsWith('www.')) v = v.slice(4);
    return v.replace(/^[.\s]+|[.\s]+$/g, '');
};

/**
 * Deutsche Rufnummer auf vergleichbare Ziffern.
 * „+49 (0) 431 / 123 45-6" und „0431 12345 6" muessen denselben Wert ergeben,
 * sonst feuert das Telefon-Signal nie.
 */
export const normalizePhone = (phone?: string | null): string => {
    if (!phone) return '';
    let d = String(phone).replace(/[^\d+]/g, '');
    if (d.startsWith('+49')) d = '0' + d.slice(3);
    else if (d.startsWith('0049')) d = '0' + d.slice(4);
    d = d.replace(/^\++/, '');
    // Manche Quellen schreiben „+49 (0)431" -> „00431": die Doppelnull falten.
    if (d.startsWith('00') && !d.startsWith('000')) d = d.slice(1);
    return d;
};

/** Fuenfstellige deutsche PLZ aus einem Freitext, sonst ''. */
export const normalizeZip = (value?: string | null): string => {
    if (!value) return '';
    const m = /\b\d{5}\b/.exec(String(value));
    return m ? m[0] : '';
};

/**
 * Multi-Signal-Fingerprint (Dedupe-Ebene 2).
 *
 * Die vier Signale werden in FESTER Reihenfolge verbunden; leere Signale
 * bleiben als leeres Segment stehen statt zu entfallen — sonst koennte ein
 * Datensatz mit Domain mit einem kollidieren, der nur einen Namen hat.
 *
 * '' ohne verwertbaren Namen: ein Fingerprint ueber nichts machte jeden leeren
 * Datensatz mit jedem anderen identisch.
 *
 * BEWUSSTE ABWEICHUNG von normalize.py: dort wird der zusammengesetzte String
 * per sha256 gehasht, hier bleibt er im Klartext. Grund: im Browser gibt es
 * kein synchrones sha256 (crypto.subtle ist asynchron und nur im Secure
 * Context), eine pure Funktion waere damit unmoeglich. Der Wert ist ein
 * Vergleichsschluessel, kein Geheimnis — die GLEICHHEITSSEMANTIK ist identisch,
 * und im Klartext ist im Review sofort sichtbar, WORAN zwei Datensaetze
 * haengen.
 */
export const fingerprintLead = (
    name?: string | null,
    zip?: string | null,
    website?: string | null,
    phone?: string | null,
): string => {
    const kern = normalizeLeadName(name);
    if (!kern) return '';
    return [kern, normalizeZip(zip), normalizeDomain(website), normalizePhone(phone)].join('|');
};

// ═══ TEIL 2 — KANDIDATEN AUS ROHDATEN ═══════════════════════════════════════

/** Eine Zeile aus public.leads_raw, so wie sie gelesen wird. */
export interface LeadsRawRow {
    id: string;
    source: string;
    source_record_id: string;
    payload: Record<string, any>;
    payload_hash?: string;
    parser_version?: string;
    first_seen_at?: string;
    last_seen_at?: string;
}

/** Der aus einem Payload abgeleitete Lead-Kandidat (noch nichts entschieden). */
export interface LeadCandidate {
    rawId: string;
    quelle: string;
    quelleUrl?: string;
    name: string;
    branche?: string;
    street?: string;
    zip?: string;
    city?: string;
    bundesland?: string;
    website?: string;
    email?: string;
    phone?: string;
    ansprechpartner?: string;
    fingerprint: string;
}

/** Der aus einem Payload abgeleitete Ausschreibungs-Kandidat. */
export interface TenderCandidate {
    rawId: string;
    source: string;
    sourceRecordId: string;
    titel: string;
    auftraggeber?: string;
    cpvCodes?: string[];
    verfahrensart?: string;
    leistungsort?: string;
    zip?: string;
    bundesland?: string;
    veroeffentlichtAm?: string;
    abgabefrist?: string;
    dokumenteUrl?: string;
}

/** Erster nicht-leerer String unter mehreren Schluesseln. */
const pick = (payload: Record<string, any>, ...keys: string[]): string | undefined => {
    for (const k of keys) {
        const v = payload?.[k];
        if (typeof v === 'string' && v.trim()) return v.trim();
        if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    }
    return undefined;
};

/**
 * Ausschreibung oder Firma?
 *
 * Eine Ausschreibung ist KEIN Lead (Plan 5, Codex-Befund 2.6): sie hat eine
 * Abgabefrist, CPV-Codes und einen Auftraggeber, der oft schon Kunde ist. Als
 * Lead gefuehrt haette sie einen Lebenszyklus, der nicht zu ihr passt.
 *
 * Entscheidend ist `payload.kind`; hilfsweise verraten sich Ausschreibungen
 * ueber Abgabefrist bzw. CPV-Codes, falls eine Quelle das Feld nicht setzt.
 */
export const classifyRawKind = (payload?: Record<string, any> | null): 'lead' | 'ausschreibung' => {
    const kind = (payload?.kind || payload?.art || '').toString().trim().toLowerCase();
    if (kind === 'ausschreibung' || kind === 'tender') return 'ausschreibung';
    if (kind === 'lead' || kind === 'firma' || kind === 'company') return 'lead';
    if (payload?.abgabefrist || payload?.deadline) return 'ausschreibung';
    if (Array.isArray(payload?.cpv_codes) && payload.cpv_codes.length) return 'ausschreibung';
    return 'lead';
};

/** Branche nur uebernehmen, wenn sie zum kontrollierten Vokabular gehoert. */
const brancheOrUndefined = (value?: string): string | undefined =>
    value && isBranche(value) ? value : undefined;

/**
 * Rohzeile -> Lead-Kandidat. `null`, wenn kein verwertbarer Name uebrig
 * bleibt: ein namenloser Lead ist kein Lead, sondern ein Parser-Fehler, und er
 * wuerde jede Dublettenpruefung vergiften.
 *
 * Die Feldnamen werden tolerant gelesen (deutsch und englisch): jede Quelle
 * bringt ihre eigene Schreibweise mit, und eine Quelle darf nicht deshalb
 * ausfallen, weil sie „strasse" statt „street" schreibt.
 */
export const rawToLeadCandidate = (raw: LeadsRawRow): LeadCandidate | null => {
    const p = raw?.payload || {};
    const name = pick(p, 'name', 'firma', 'company', 'unternehmen', 'titel', 'title');
    if (!name) return null;

    const zip = normalizeZip(pick(p, 'zip', 'plz', 'postcode', 'postleitzahl') || pick(p, 'adresse', 'address'));
    const website = pick(p, 'website', 'url', 'homepage', 'web');
    const phone = pick(p, 'phone', 'telefon', 'tel');

    return {
        rawId: raw.id,
        quelle: `scraper:${raw.source}`,
        quelleUrl: pick(p, 'quelle_url', 'source_url', 'permalink', 'link') || undefined,
        name,
        branche: brancheOrUndefined(pick(p, 'branche', 'branch', 'kategorie')),
        street: pick(p, 'street', 'strasse', 'straße'),
        zip: zip || undefined,
        city: pick(p, 'city', 'ort', 'stadt'),
        bundesland: pick(p, 'bundesland', 'state', 'region'),
        website,
        email: pick(p, 'email', 'e_mail', 'mail'),
        phone,
        ansprechpartner: pick(p, 'ansprechpartner', 'contact', 'kontakt'),
        fingerprint: fingerprintLead(name, zip, website, phone),
    };
};

/** Rohzeile -> Ausschreibungs-Kandidat. `null` ohne Titel. */
export const rawToTenderCandidate = (raw: LeadsRawRow): TenderCandidate | null => {
    const p = raw?.payload || {};
    const titel = pick(p, 'titel', 'title', 'bezeichnung', 'name');
    if (!titel) return null;

    const cpv = p.cpv_codes ?? p.cpvCodes ?? p.cpv;
    return {
        rawId: raw.id,
        source: raw.source,
        sourceRecordId: raw.source_record_id,
        titel,
        auftraggeber: pick(p, 'auftraggeber', 'vergabestelle', 'buyer', 'contracting_authority'),
        cpvCodes: Array.isArray(cpv) ? cpv.map(String).filter(Boolean) : undefined,
        verfahrensart: pick(p, 'verfahrensart', 'procedure', 'verfahren'),
        leistungsort: pick(p, 'leistungsort', 'ort', 'place'),
        zip: normalizeZip(pick(p, 'zip', 'plz') || pick(p, 'leistungsort', 'ort')) || undefined,
        bundesland: pick(p, 'bundesland', 'state'),
        veroeffentlichtAm: pick(p, 'veroeffentlicht_am', 'published_at', 'datum'),
        abgabefrist: pick(p, 'abgabefrist', 'deadline', 'frist'),
        dokumenteUrl: pick(p, 'dokumente_url', 'documents_url', 'link', 'url'),
    };
};

// ═══ TEIL 3 — ABGLEICH GEGEN BESTAND ════════════════════════════════════════

/** Das Minimum, das ein Bestands-Lead fuer den Abgleich mitbringen muss. */
export interface ExistingLeadLike {
    id: string;
    name: string;
    zip?: string | null;
    website?: string | null;
    phone?: string | null;
}

/** Dasselbe fuer einen Bestandskunden. */
export interface ExistingCustomerLike {
    id: string;
    name: string;
    zip?: string | null;
    website?: string | null;
    phone?: string | null;
    archivedAt?: string | null;
}

export type MatchStatus = 'auto_neu' | 'auto_zugeordnet' | 'offen';

export interface MatchResult {
    status: MatchStatus;
    /** 0..1, auf drei Nachkommastellen gerundet (Spalte numeric(4,3)). */
    confidence: number;
    matchedLeadId?: string;
    matchedCustomerId?: string;
    /** Welche Signale gefeuert haben — steht so im Review-Dialog. */
    reasons: string[];
}

/**
 * Ab hier wird OHNE Rueckfrage zugeordnet. Hoch angesetzt: eine falsche
 * automatische Zuordnung verschmilzt zwei Firmen still, und das faellt erst
 * auf, wenn jemand den falschen Ansprechpartner anruft.
 */
export const AUTO_ZUORDNEN_AB = 0.9;

/**
 * Ab hier lohnt der Blick eines Menschen. Darunter wird als neuer Lead
 * angelegt — ein zusaetzlicher Datensatz ist der billigere Fehler als eine
 * uebersehene Firma.
 */
export const REVIEW_AB = 0.5;

/** Ein einzelner Treffer waehrend der Bewertung. */
interface Treffer {
    id: string;
    art: 'lead' | 'customer';
    confidence: number;
    reasons: string[];
}

const bewerte = (
    kandidat: Pick<LeadCandidate, 'name' | 'zip' | 'website' | 'phone'>,
    bestand: { name: string; zip?: string | null; website?: string | null; phone?: string | null },
): { confidence: number; reasons: string[] } => {
    const reasons: string[] = [];
    let confidence = 0;

    // ── Domain: das staerkste Einzelsignal ───────────────────────────────────
    const domA = normalizeDomain(kandidat.website);
    const domB = normalizeDomain(bestand.website);
    if (domA && domB && domA === domB) {
        confidence = Math.max(confidence, 0.95);
        reasons.push(`gleiche Web-Domain (${domA})`);
    }

    // ── Telefon: stark, aber Zentralnummern grosser Konzerne teilen sich
    //    Toechter — darum knapp unter der Domain.
    const telA = normalizePhone(kandidat.phone);
    const telB = normalizePhone(bestand.phone);
    if (telA && telB && telA === telB) {
        confidence = Math.max(confidence, 0.9);
        reasons.push('gleiche Rufnummer');
    }

    // ── Name (+ PLZ) ─────────────────────────────────────────────────────────
    // nameSimilarity kommt aus customerDuplicates: dieselbe Token-/Bigramm-
    // Heuristik, die die Kunden-Dublettenpruefung seit dem Kanzleilisten-Import
    // benutzt. Bewusst wiederverwendet statt nachgebaut — zwei Heuristiken, die
    // auseinanderlaufen, waeren schlimmer als eine unvollkommene.
    const sim = nameSimilarity(kandidat.name || '', bestand.name || '');
    const zipA = normalizeZip(kandidat.zip);
    const zipB = normalizeZip(bestand.zip);
    const gleichePlz = Boolean(zipA && zipB && zipA === zipB);

    if (sim >= 0.99 && gleichePlz) {
        confidence = Math.max(confidence, 0.95);
        reasons.push('gleicher Name', 'gleiche PLZ');
    } else if (sim >= 0.99) {
        confidence = Math.max(confidence, 0.8);
        reasons.push('gleicher Name');
    } else if (sim >= 0.6 && gleichePlz) {
        confidence = Math.max(confidence, 0.75);
        reasons.push('ähnlicher Name', 'gleiche PLZ');
    } else if (sim >= 0.6) {
        confidence = Math.max(confidence, 0.55);
        reasons.push('ähnlicher Name');
    } else if (sim > 0 && gleichePlz) {
        // Schon EIN gemeinsames Namens-Token am selben Ort ist verdaechtig
        // („Musterbau Hoch- und Tiefbau" neben „Musterbau GmbH" in 20095).
        confidence = Math.max(confidence, 0.55);
        reasons.push('teilweise gleicher Name', 'gleiche PLZ');
    }

    return { confidence, reasons };
};

/**
 * Kandidat gegen bestehende Leads UND Kunden pruefen.
 *
 * Beide Bestaende, weil eine gescrapte Firma ebenso gut ein Bestandskunde sein
 * kann — den als „neuen Lead" aufzunehmen waere der peinlichste Fehler des
 * Moduls (Akquise-Anruf beim eigenen Kunden).
 *
 * Bei Gleichstand gewinnt der KUNDE: er traegt Nummer, Rechnungen und
 * Historie, der Lead ist nur die Vorstufe.
 */
export const matchCandidate = (
    kandidat: Pick<LeadCandidate, 'name' | 'zip' | 'website' | 'phone'>,
    leads: ExistingLeadLike[] | null | undefined,
    customers: ExistingCustomerLike[] | null | undefined,
): MatchResult => {
    const treffer: Treffer[] = [];

    for (const c of customers || []) {
        const { confidence, reasons } = bewerte(kandidat, c);
        if (confidence <= 0) continue;
        // Archivierte MITPRUEFEN: sonst legt der Import den Lead zu der Firma
        // an, die gestern archiviert wurde (gleiche Regel wie
        // findSimilarCustomers).
        if (c.archivedAt) reasons.push('Kunde ist archiviert');
        treffer.push({ id: c.id, art: 'customer', confidence, reasons });
    }

    for (const l of leads || []) {
        const { confidence, reasons } = bewerte(kandidat, l);
        if (confidence <= 0) continue;
        treffer.push({ id: l.id, art: 'lead', confidence, reasons });
    }

    if (!treffer.length) {
        return { status: 'auto_neu', confidence: 0, reasons: ['kein Treffer im Bestand'] };
    }

    treffer.sort((a, b) => {
        if (b.confidence !== a.confidence) return b.confidence - a.confidence;
        // Gleichstand: Kunde vor Lead.
        if (a.art !== b.art) return a.art === 'customer' ? -1 : 1;
        return 0;
    });
    const best = treffer[0];
    const confidence = Math.round(best.confidence * 1000) / 1000;

    const status: MatchStatus =
        confidence >= AUTO_ZUORDNEN_AB ? 'auto_zugeordnet' :
        confidence >= REVIEW_AB ? 'offen' :
        'auto_neu';

    // Unterhalb der Review-Schwelle wird der schwache Treffer verworfen: ihn
    // trotzdem zu verknuepfen waere geraten.
    if (status === 'auto_neu') {
        return { status, confidence, reasons: ['nur schwache Ähnlichkeit — als neuer Lead behandelt'] };
    }

    return {
        status,
        confidence,
        matchedLeadId: best.art === 'lead' ? best.id : undefined,
        matchedCustomerId: best.art === 'customer' ? best.id : undefined,
        reasons: best.reasons,
    };
};

// ═══ TEIL 4 — IMPORTPLAN ════════════════════════════════════════════════════

export interface PlannedLead {
    candidate: LeadCandidate;
    match: MatchResult;
}

export interface ImportPlan {
    /** Kein Treffer -> wird als Lead angelegt. */
    neu: PlannedLead[];
    /** Hohe Konfidenz -> nur lead_matches, KEIN neuer Lead. */
    zugeordnet: PlannedLead[];
    /** Mittlere Konfidenz -> Review-UI entscheidet. */
    offen: PlannedLead[];
    /** kind = 'ausschreibung' -> tender_opportunities statt leads. */
    ausschreibungen: TenderCandidate[];
    /** Rohzeilen ohne verwertbaren Inhalt (mit Begruendung). */
    verworfen: { rawId: string; grund: string }[];
}

/**
 * Der vollstaendige Plan zu einer Menge Rohzeilen — pur und damit als Ganzes
 * pruefbar. Nichts wird hier geschrieben; das erledigt applyImportPlan.
 *
 * WICHTIG: bereits geplante Kandidaten wandern in den Vergleichsbestand. Ohne
 * das lieferte ein Lauf, in dem dieselbe Firma zweimal auftaucht (zwei
 * Quellen, zwei Schreibweisen), auch zwei neue Leads — die Dublette entstuende
 * also GENAU durch den Import, der sie verhindern soll.
 */
export const planLeadImport = (
    raws: LeadsRawRow[] | null | undefined,
    leads: ExistingLeadLike[] | null | undefined,
    customers: ExistingCustomerLike[] | null | undefined,
): ImportPlan => {
    const plan: ImportPlan = { neu: [], zugeordnet: [], offen: [], ausschreibungen: [], verworfen: [] };
    const bestandLeads: ExistingLeadLike[] = [...(leads || [])];
    // Fingerprints bereits geplanter Neuanlagen — der billige Vorfilter vor
    // der teureren Aehnlichkeitsrechnung.
    const geplanteFingerprints = new Map<string, LeadCandidate>();

    for (const raw of raws || []) {
        if (!raw?.id) continue;

        if (classifyRawKind(raw.payload) === 'ausschreibung') {
            const tender = rawToTenderCandidate(raw);
            if (tender) plan.ausschreibungen.push(tender);
            else plan.verworfen.push({ rawId: raw.id, grund: 'Ausschreibung ohne Titel' });
            continue;
        }

        const candidate = rawToLeadCandidate(raw);
        if (!candidate) {
            plan.verworfen.push({ rawId: raw.id, grund: 'kein verwertbarer Firmenname im Payload' });
            continue;
        }

        // Identischer Fingerprint innerhalb desselben Laufs: dieselbe Firma,
        // zweimal beobachtet.
        const zwilling = candidate.fingerprint ? geplanteFingerprints.get(candidate.fingerprint) : undefined;
        if (zwilling) {
            plan.verworfen.push({
                rawId: raw.id,
                grund: `identischer Fingerprint wie „${zwilling.name}" aus demselben Lauf`,
            });
            continue;
        }

        const match = matchCandidate(candidate, bestandLeads, customers);
        const eintrag: PlannedLead = { candidate, match };

        if (match.status === 'auto_zugeordnet') {
            plan.zugeordnet.push(eintrag);
        } else if (match.status === 'offen') {
            plan.offen.push(eintrag);
        } else {
            plan.neu.push(eintrag);
            if (candidate.fingerprint) geplanteFingerprints.set(candidate.fingerprint, candidate);
            // Der geplante Lead ist ab jetzt Bestand — die id ist noch nicht
            // vergeben, deshalb der raw-Bezug als Platzhalter.
            bestandLeads.push({
                id: `geplant:${raw.id}`,
                name: candidate.name,
                zip: candidate.zip,
                website: candidate.website,
                phone: candidate.phone,
            });
        }
    }

    return plan;
};

/** Kurzfassung fuer die Oberflaeche („12 neu, 3 zugeordnet, 2 zu prüfen"). */
export const summarizeImportPlan = (plan: ImportPlan): string => {
    const teile = [
        `${plan.neu.length} neu`,
        `${plan.zugeordnet.length} zugeordnet`,
        `${plan.offen.length} zu prüfen`,
    ];
    if (plan.ausschreibungen.length) teile.push(`${plan.ausschreibungen.length} Ausschreibungen`);
    if (plan.verworfen.length) teile.push(`${plan.verworfen.length} verworfen`);
    return teile.join(', ');
};

// ═══ TEIL 4b — DATEI-IMPORT (PARSEN, PUR) ═══════════════════════════════════
// Der Lead-Scout schreibt nicht mehr selbst in die Datenbank (kein
// service_role-Key). Er exportiert seine Treffer in eine JSON-Datei, die der
// Betreiber hier hochlaedt; erst die App legt sie unter der Nutzer-Session in
// leads_raw ab. Ab dort laeuft der bestehende plan/apply-Fluss unveraendert.

/** Ein geparster Export: Quelle + die rohen Datensaetze (spaeter der Payload). */
export interface LeadScoutExport {
    source: string;
    records: Record<string, any>[];
}

/** Kennung des Dateiformats; die App akzeptiert jede Minor-Version darunter. */
export const LEAD_SCOUT_EXPORT_PREFIX = 'lead-scout-export/';

/**
 * Eine Export-Datei einlesen und validieren. Pur — kein Supabase, keine Uhr.
 *
 * Erwartet die Huelle `{ format, source, records }` aus lead-scout/app/export.py.
 * Tolerant auch gegenueber einem blanken Array von Datensaetzen (dann wird die
 * Quelle aus dem ersten Record gezogen), damit eine von Hand zusammengestellte
 * Datei nicht an der Huelle scheitert.
 *
 * Wirft mit sprechender deutscher Meldung bei kaputtem JSON, falschem Format
 * oder leerer Datensatzliste — der Dialog reicht die Meldung ueber describeError
 * durch.
 */
export const parseLeadScoutExport = (jsonText: string): LeadScoutExport => {
    let data: any;
    try {
        data = JSON.parse(jsonText);
    } catch {
        throw new Error('Die Datei ist kein gültiges JSON.');
    }

    let records: any[];
    let source: string | undefined;

    if (Array.isArray(data)) {
        // Blankes Array: die Quelle steckt dann nur in den Datensaetzen selbst.
        records = data;
    } else if (data && typeof data === 'object') {
        const format = typeof data.format === 'string' ? data.format : '';
        if (!format.startsWith(LEAD_SCOUT_EXPORT_PREFIX)) {
            throw new Error(
                'Unerwartetes Dateiformat — erwartet wird ein Lead-Scout-Export '
                + '(Feld „format" beginnt mit „lead-scout-export/").',
            );
        }
        if (!Array.isArray(data.records)) {
            throw new Error('Die Datei enthält kein „records"-Feld mit Datensätzen.');
        }
        records = data.records;
        source = typeof data.source === 'string' && data.source.trim() ? data.source.trim() : undefined;
    } else {
        throw new Error('Unerwartetes Dateiformat — erwartet wird ein Lead-Scout-Export.');
    }

    records = records.filter((r) => r && typeof r === 'object' && !Array.isArray(r));
    if (!records.length) {
        throw new Error('Die Datei enthält keine Datensätze.');
    }

    if (!source) {
        const first = records[0]?.source;
        source = typeof first === 'string' && first.trim() ? first.trim() : undefined;
    }
    if (!source) {
        throw new Error('Die Datei nennt keine Quelle (Feld „source").');
    }

    return { source, records: records as Record<string, any>[] };
};

// ═══ TEIL 5 — UNREINER TEIL (Supabase) ══════════════════════════════════════
// Ab hier wird gelesen und geschrieben. Alles darueber bleibt ohne
// Infrastruktur pruefbar; diese Naht ist bewusst duenn gehalten und enthaelt
// keine Entscheidungslogik.

/**
 * Stabiler JSON-Text eines Payloads fuer den Hash: rekursiv sortierte
 * Schluessel, damit derselbe Datensatz unabhaengig von der Feldreihenfolge
 * denselben Hash ergibt.
 */
const stableStringify = (value: any): string => {
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
    const keys = Object.keys(value).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
};

/** Ergebnis des Datei-Stagings nach leads_raw. */
export interface StageRawResult {
    /** In leads_raw neu angelegte Zeilen. */
    angelegt: number;
    /** Wegen (source, source_record_id)-Dublette uebersprungen. */
    uebersprungen: number;
}

/**
 * Datei-Datensaetze als Rohbeobachtungen nach leads_raw schreiben — der Ersatz
 * fuer den DB-schreibenden Worker. Laeuft unter der NUTZER-Session (Policy
 * `leads_raw_internal_all` fuer authenticated), NICHT mit einem service_role-Key:
 * der Scraper hat die Datei nur exportiert, geschrieben wird erst hier.
 *
 * Jeder Record wird 1:1 zum `payload` (dieselben Feldnamen, die der Worker
 * schriebe), sodass der anschliessende plan/apply-Fluss (planLeadImport aus
 * fetchUnresolvedRaw) ihn unveraendert verarbeitet.
 *
 * Upsert auf (source, source_record_id) mit ignoreDuplicates: dieselbe Datei
 * zweimal hochgeladen erzeugt keine Dubletten. Fehlt die Satz-ID, wird sie
 * deterministisch aus Name + PLZ abgeleitet, damit derselbe Datensatz stabil
 * bleibt.
 *
 * `companyId` wird fuer die Aufrufsymmetrie mit applyImportPlan mitgefuehrt;
 * leads_raw ist heute (wie im SQL vermerkt) NICHT company-scoped, die Spalte
 * existiert dort nicht — deshalb wird sie nicht geschrieben.
 */
export const stageRawFromFile = async (
    records: Record<string, any>[],
    source: string,
    companyId: string,
): Promise<StageRawResult> => {
    void companyId; // s. Doku: leads_raw hat keine company_id-Spalte.
    const jetzt = new Date().toISOString();

    // Innerhalb der Datei nach (source, source_record_id) entdoppeln, damit die
    // Zaehlung stimmt und der Upsert keinen Schluessel doppelt anfasst.
    const gesehen = new Set<string>();
    const rows: Record<string, any>[] = [];
    for (const r of records || []) {
        if (!r || typeof r !== 'object') continue;
        const rowSource = typeof r.source === 'string' && r.source.trim() ? r.source.trim() : source;
        const rawSrid = typeof r.source_record_id === 'string' && r.source_record_id.trim()
            ? r.source_record_id.trim()
            : '';
        const srid = rawSrid || `file:${addressHash(
            `${normalizeLeadName(pick(r, 'name', 'firma', 'company', 'titel', 'title'))}|`
            + `${normalizeZip(pick(r, 'zip', 'plz', 'postleitzahl'))}`,
        )}`;

        const key = `${rowSource} ${srid}`;
        if (gesehen.has(key)) continue;
        gesehen.add(key);

        rows.push({
            source: rowSource,
            source_record_id: srid,
            payload: r,
            payload_hash: addressHash(stableStringify(r)),
            parser_version: 'file-import/1',
            first_seen_at: jetzt,
            last_seen_at: jetzt,
        });
    }

    let angelegt = 0;
    const CHUNK = 500;
    for (let i = 0; i < rows.length; i += CHUNK) {
        const chunk = rows.slice(i, i + CHUNK);
        // ignoreDuplicates -> ON CONFLICT DO NOTHING: nur wirklich neue Zeilen
        // kommen zurueck, der Rest existiert bereits (frueherer Upload/Lauf).
        const { data, error } = await supabase
            .from('leads_raw')
            .upsert(chunk, { onConflict: 'source,source_record_id', ignoreDuplicates: true })
            .select('id');
        if (error) throw new Error(describeError(error));
        angelegt += (data || []).length;
    }

    return { angelegt, uebersprungen: rows.length - angelegt };
};

/**
 * Noch nicht entschiedene Rohbeobachtungen, die neuesten zuerst, hoechstens
 * `limit`.
 *
 * Zwei Abfragen statt eines Joins: PostgREST kann „nicht vorhanden in
 * Tabelle B" nicht ausdruecken, und `not.in` mit tausenden IDs sprengt die
 * URL-Laenge. Die bereits entschiedenen raw_ids sind klein genug fuer eine
 * Menge im Speicher.
 *
 * Beide seitenweise (Vorfall 23.10.2026: PostgREST kappt ohne `.range()`
 * still bei 1000). Frueher sollte `.limit(limit + bekannt.size)` genug Zeilen
 * holen, wurde aber selbst bei 1000 gekappt: ab 1000 entschiedenen neuesten
 * Rohzeilen kam kein offener Datensatz mehr an. Jetzt wird leads_raw nur so
 * weit durchgeblaettert, bis `limit` offene beisammen sind.
 */
export const fetchUnresolvedRaw = async (limit = 500): Promise<LeadsRawRow[]> => {
    const entschieden = await ladeAlleSeiten<{ raw_id: string }>((von, bis) =>
        supabase.from('lead_matches').select('raw_id').order('id').range(von, bis))
        .catch((error: unknown) => {
            throw new Error(describeError(error));
        });
    const bekannt = new Set(entschieden.map((r) => r.raw_id));

    const offen: LeadsRawRow[] = [];
    // Rutscht eine Zeile zwischen zwei Seiten nach vorn (neues last_seen_at),
    // kaeme sie sonst doppelt in den Plan.
    const gesehen = new Set<string>();
    for (let von = 0; offen.length < limit; von += ZEILEN_JE_SEITE) {
        const { data, error } = await supabase
            .from('leads_raw')
            .select('*')
            .order('last_seen_at', { ascending: false })
            .order('id')
            .range(von, von + ZEILEN_JE_SEITE - 1);
        if (error) throw new Error(describeError(error));
        const seite = (data || []) as LeadsRawRow[];
        for (const r of seite) {
            if (offen.length >= limit) break;
            if (bekannt.has(r.id) || gesehen.has(r.id)) continue;
            gesehen.add(r.id);
            offen.push(r);
        }
        if (seite.length < ZEILEN_JE_SEITE) break;
    }
    return offen;
};

/**
 * Bestand fuer den Abgleich — schlank selektiert, nur die Match-Signale.
 *
 * Seitenweise: customers hat 1244 Zeilen (PROD 23.10.2026); ohne `.range()`
 * sah der Abgleich nur 1000 davon und konnte Bestandskunden als neue Leads
 * anlegen.
 */
export const fetchMatchBaseline = async (): Promise<{
    leads: ExistingLeadLike[];
    customers: ExistingCustomerLike[];
}> => {
    const [leadRows, custRows] = await Promise.all([
        ladeAlleSeiten<ExistingLeadLike>((von, bis) =>
            supabase.from('leads').select('id, name, zip, website, phone').order('id').range(von, bis)),
        ladeAlleSeiten<{ id: string; name: string; zip?: string | null; website?: string | null; archived_at?: string | null }>(
            (von, bis) =>
                supabase.from('customers').select('id, name, zip, website, archived_at').order('id').range(von, bis),
        ),
    ]).catch((error: unknown) => {
        throw new Error(describeError(error));
    });

    return {
        leads: leadRows,
        customers: custRows.map((c) => ({
            id: c.id,
            name: c.name,
            zip: c.zip,
            website: c.website,
            // `customers` hat keine eigene Telefonspalte im Match-Umfang —
            // das Signal entfaellt dort und wird nicht erfunden.
            phone: null,
            archivedAt: c.archived_at,
        })),
    };
};

export interface ApplyResult {
    angelegt: number;
    zugeordnet: number;
    zurReview: number;
    ausschreibungen: number;
    fehler: { rawId: string; meldung: string }[];
}

/**
 * Plan ausfuehren: Leads anlegen, lead_matches schreiben, Ausschreibungen in
 * tender_opportunities uebernehmen.
 *
 * Je Datensatz einzeln abgesichert: eine kaputte Zeile darf den ganzen Lauf
 * nicht kippen. Die Fehler kommen gesammelt zurueck und stehen anschliessend
 * in der Oberflaeche — still verschluckt wuerde niemand sie je bemerken.
 */
export const applyImportPlan = async (plan: ImportPlan, companyId: string): Promise<ApplyResult> => {
    const result: ApplyResult = {
        angelegt: 0, zugeordnet: 0, zurReview: 0, ausschreibungen: 0, fehler: [],
    };

    const schreibeMatch = async (
        rawId: string,
        matchStatus: string,
        confidence: number,
        matchedLeadId?: string,
        matchedCustomerId?: string,
    ) => {
        const { error } = await supabase.from('lead_matches').insert([{
            raw_id: rawId,
            matched_lead_id: matchedLeadId ?? null,
            matched_customer_id: matchedCustomerId ?? null,
            match_status: matchStatus,
            confidence,
        }]);
        if (error) throw new Error(describeError(error));
    };

    for (const eintrag of plan.neu) {
        const c = eintrag.candidate;
        try {
            const { data, error } = await supabase.from('leads').insert([{
                company_id: companyId,
                name: c.name,
                branche: c.branche ?? null,
                street: c.street ?? null,
                zip: c.zip ?? null,
                city: c.city ?? null,
                bundesland: c.bundesland ?? null,
                website: c.website ?? null,
                email: c.email ?? null,
                phone: c.phone ?? null,
                ansprechpartner: c.ansprechpartner ?? null,
                quelle: c.quelle,
                quelle_url: c.quelleUrl ?? null,
                status: 'neu',
            }]).select('id').single();
            if (error) throw new Error(describeError(error));
            await schreibeMatch(c.rawId, 'auto_neu', eintrag.match.confidence, data.id);
            result.angelegt += 1;
        } catch (e) {
            result.fehler.push({ rawId: c.rawId, meldung: describeError(e) });
        }
    }

    for (const eintrag of plan.zugeordnet) {
        try {
            await schreibeMatch(
                eintrag.candidate.rawId, 'auto_zugeordnet', eintrag.match.confidence,
                eintrag.match.matchedLeadId, eintrag.match.matchedCustomerId,
            );
            result.zugeordnet += 1;
        } catch (e) {
            result.fehler.push({ rawId: eintrag.candidate.rawId, meldung: describeError(e) });
        }
    }

    for (const eintrag of plan.offen) {
        try {
            await schreibeMatch(
                eintrag.candidate.rawId, 'offen', eintrag.match.confidence,
                eintrag.match.matchedLeadId, eintrag.match.matchedCustomerId,
            );
            result.zurReview += 1;
        } catch (e) {
            result.fehler.push({ rawId: eintrag.candidate.rawId, meldung: describeError(e) });
        }
    }

    for (const t of plan.ausschreibungen) {
        try {
            // Upsert auf (source, source_record_id): dieselbe Ausschreibung
            // taucht bei jedem Lauf der Quelle erneut auf.
            const { error } = await supabase.from('tender_opportunities').upsert([{
                company_id: companyId,
                source: t.source,
                source_record_id: t.sourceRecordId,
                titel: t.titel,
                auftraggeber: t.auftraggeber ?? null,
                cpv_codes: t.cpvCodes ?? null,
                verfahrensart: t.verfahrensart ?? null,
                leistungsort: t.leistungsort ?? null,
                zip: t.zip ?? null,
                bundesland: t.bundesland ?? null,
                veroeffentlicht_am: t.veroeffentlichtAm ?? null,
                abgabefrist: t.abgabefrist ?? null,
                dokumente_url: t.dokumenteUrl ?? null,
            }], { onConflict: 'source,source_record_id', ignoreDuplicates: true });
            if (error) throw new Error(describeError(error));
            result.ausschreibungen += 1;
        } catch (e) {
            result.fehler.push({ rawId: t.rawId, meldung: describeError(e) });
        }
    }

    return result;
};

/**
 * Offene Entscheidungen fuer das Review (LeadMatchReviewModal).
 *
 * Seitenweise: ein grosser Scout-Lauf kann mehr als 1000 offene Faelle
 * hinterlassen, die PostgREST ohne `.range()` still abschnitte (Vorfall
 * 23.10.2026).
 */
export const fetchOpenMatches = async (): Promise<any[]> =>
    ladeAlleSeiten<Record<string, unknown>>((von, bis) =>
        supabase
            .from('lead_matches')
            .select('*, leads_raw(source, source_record_id, payload), leads(name, zip, city), customers(name, zip, city)')
            .eq('match_status', 'offen')
            .order('created_at', { ascending: true })
            .order('id')
            .range(von, bis))
        .catch((error: unknown) => {
            throw new Error(describeError(error));
        });

/**
 * Entscheidung im Review festschreiben.
 * 'manuell_bestaetigt' = die Zuordnung stimmt, 'verworfen' = kein Bezug.
 */
export const resolveMatch = async (
    matchId: string,
    entscheidung: 'manuell_bestaetigt' | 'verworfen',
): Promise<void> => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
        .from('lead_matches')
        .update({ match_status: entscheidung, reviewed_by: auth?.user?.id ?? null })
        .eq('id', matchId);
    if (error) throw new Error(describeError(error));
};
